import { useState } from 'react';
import { useQuery, useMutation, gql } from '@apollo/client';
import { Button, Input, Label, Switch, Textarea } from '@artstorefronts/ui';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

import AdminLayout from '../../../components/AdminLayout';

const ADMIN_REWARDS_QUERY = gql`
  query AdminRedemptionRewards {
    adminRedemptionRewards {
      id
      slug
      title
      pointCost
      rewardType
      isRedeemable
      status
      notBuyableReason
      priceLookupKey
    }
    adminRedemptions(first: 50) {
      id
      pointsSpent
      status
      createdAt
      subscriptionId
      stuckAt
      reward {
        slug
        title
      }
      user {
        asfId
        email
        firstName
        lastName
      }
    }
  }
`;

const UPDATE_REWARD = gql`
  mutation AdminUpdateRedemptionReward($id: ID!, $input: AdminRedemptionRewardInput!) {
    adminUpdateRedemptionReward(id: $id, input: $input) {
      id
      pointCost
      status
      isRedeemable
      notBuyableReason
    }
  }
`;

const SYNC_FROM_STRIPE = gql`
  mutation AdminSyncRedemptionRewards {
    adminSyncRedemptionRewards {
      created
      skipped
      drafts {
        slug
        title
      }
    }
  }
`;

const REFUND = gql`
  mutation AdminRefundRedemption($redemptionId: ID!, $reason: String!) {
    adminRefundRedemption(redemptionId: $redemptionId, reason: $reason)
  }
`;

const CLAWBACK = gql`
  mutation AdminClawbackRedemption($redemptionId: ID!, $reason: String!) {
    adminClawbackRedemption(redemptionId: $redemptionId, reason: $reason)
  }
`;

interface Reward {
  id: string;
  slug: string;
  title: string;
  pointCost: number;
  rewardType: string;
  isRedeemable: boolean;
  status: string;
  notBuyableReason: string | null;
  priceLookupKey: string | null;
}

interface Redemption {
  id: string;
  pointsSpent: number;
  status: string;
  createdAt: string;
  subscriptionId: string | null;
  stuckAt: string | null;
  reward: { slug: string; title: string } | null;
  user: { asfId: string; email: string; firstName: string | null; lastName: string | null } | null;
}

function RewardRow({ reward, onSaved }: { reward: Reward; onSaved: () => void }) {
  const [pointCost, setPointCost] = useState(String(reward.pointCost));
  const [update, { loading }] = useMutation(UPDATE_REWARD);

  const apply = async (input: Record<string, unknown>, message: string) => {
    try {
      await update({ variables: { id: reward.id, input } });
      toast.success(message);
      onSaved();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Update failed');
    }
  };

  // Off is `draft`, not `retired` — retiring soft-deletes, which this list does not show and a
  // switch should not do. So the toggle only ever moves a reward between the two states it can
  // safely bounce between, and switching off is always undoable.
  const nextStatus = reward.status === 'active' ? 'draft' : 'active';

  return (
    <tr className="border-b align-top">
      <td className="py-2 pr-4">
        <div className="font-mono text-xs">{reward.slug}</div>
        {reward.priceLookupKey && (
          <div className="text-muted-foreground font-mono text-[11px]">{reward.priceLookupKey}</div>
        )}
      </td>
      <td className="py-2 pr-4">{reward.title}</td>
      <td className="py-2 pr-4">
        <Input
          className="w-28"
          value={pointCost}
          onChange={(e) => setPointCost(e.target.value)}
          aria-label={`Point cost for ${reward.slug}`}
        />
      </td>
      <td className="py-2 pr-4">
        <div className="flex items-center gap-2">
          <Switch
            checked={reward.status === 'active'}
            aria-label={`Sell ${reward.slug}`}
            onCheckedChange={() => apply({ status: nextStatus }, `${reward.slug} is now ${nextStatus}`)}
            disabled={loading}
          />
          <span className="text-muted-foreground text-xs">{reward.status === 'active' ? 'On' : 'Off'}</span>
        </div>
        {/* Switched on and STILL not buyable is the confusing case — an active reward can be
            held back by a missing price, an inventory cap, or a start/end window, which is why
            a card with a price can read "Stay tuned" on /points. Name the blocker; a bare
            "not buyable" is not actionable. */}
        {reward.status === 'active' && !reward.isRedeemable && reward.notBuyableReason && (
          <div className="text-muted-foreground mt-1 max-w-[22rem] text-xs">{reward.notBuyableReason}</div>
        )}
      </td>
      <td className="py-2">
        <Button
          size="sm"
          onClick={() => apply({ pointCost: Number(pointCost) }, `${reward.slug} updated`)}
          disabled={loading || pointCost === String(reward.pointCost)}
        >
          Save
        </Button>
      </td>
    </tr>
  );
}

function RedemptionRow({ redemption, onChanged }: { redemption: Redemption; onChanged: () => void }) {
  const [reason, setReason] = useState('');
  const [refund, { loading: refunding }] = useMutation(REFUND);
  const [clawback, { loading: clawingBack }] = useMutation(CLAWBACK);
  const busy = refunding || clawingBack;
  const settled = redemption.status === 'failed' || redemption.status === 'reversed';

  const run = async (kind: 'refund' | 'clawback') => {
    if (!reason.trim()) {
      toast.error('Say why — it is written onto the redemption.');
      return;
    }
    try {
      const fn = kind === 'refund' ? refund : clawback;
      await fn({ variables: { redemptionId: redemption.id, reason: reason.trim() } });
      toast.success(kind === 'refund' ? 'Points returned' : 'Reward revoked, points kept');
      setReason('');
      onChanged();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed');
    }
  };

  return (
    <tr className="border-b align-top">
      <td className="py-2 pr-4 font-mono text-xs">{redemption.id}</td>
      <td className="py-2 pr-4">
        {redemption.user?.email ?? redemption.user?.asfId ?? '—'}
        <div className="text-muted-foreground text-xs">{new Date(redemption.createdAt).toLocaleString()}</div>
      </td>
      <td className="py-2 pr-4">{redemption.reward?.title ?? '—'}</td>
      <td className="py-2 pr-4">{redemption.pointsSpent.toLocaleString()}</td>
      <td className="py-2 pr-4">
        {redemption.status}
        {redemption.stuckAt && (
          <div className="mt-1">
            <span className="inline-flex items-center rounded-full border border-red-300 bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700">
              Stuck: needs review
            </span>
            <div className="text-muted-foreground text-xs">since {new Date(redemption.stuckAt).toLocaleString()}</div>
          </div>
        )}
        {redemption.subscriptionId && (
          <div className="text-muted-foreground font-mono text-xs">{redemption.subscriptionId}</div>
        )}
      </td>
      <td className="py-2">
        {settled ? (
          <span className="text-muted-foreground text-xs">closed</span>
        ) : (
          <div className="flex flex-col gap-2">
            <Textarea
              className="min-h-[44px] w-56"
              placeholder="Why?"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
            <div className="flex gap-2">
              {/* Two verbs, never one. Refund is our failure and returns the points; clawback
                  is the fraud path and deliberately keeps them, or the member just redeems again. */}
              <Button size="sm" variant="outline" disabled={busy} onClick={() => run('refund')}>
                Refund
              </Button>
              <Button size="sm" variant="destructive" disabled={busy} onClick={() => run('clawback')}>
                Clawback
              </Button>
            </div>
          </div>
        )}
      </td>
    </tr>
  );
}

export default function AdminRewardsPage() {
  const { data, loading, refetch } = useQuery<{
    adminRedemptionRewards: Reward[];
    adminRedemptions: Redemption[];
  }>(ADMIN_REWARDS_QUERY, { fetchPolicy: 'cache-and-network' });

  const [sync, { loading: syncing }] = useMutation(SYNC_FROM_STRIPE);

  const runSync = async () => {
    try {
      const result = await sync();
      const { created, skipped } = result.data.adminSyncRedemptionRewards;
      // Say what happened either way — "0 created" is a real answer, not a failure.
      toast.success(
        created === 0
          ? `Nothing new in Stripe — ${skipped} price(s) already in the catalog or not redeemable`
          : `Drafted ${created} reward(s) from Stripe. They are off sale until you take them live.`
      );
      await refetch();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Sync failed');
    }
  };

  if (loading && !data) {
    return (
      <AdminLayout title="Rewards" description="The points redemption catalog, and what members have spent.">
        <div className="flex justify-center py-12">
          <Loader2 className="animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Rewards" description="The points redemption catalog, and what members have spent.">
      <section className="mb-10 overflow-x-auto">
        <div className="mb-2 flex items-center justify-between">
          <Label className="block text-base">Catalog</Label>
          <Button variant="outline" size="sm" onClick={runSync} disabled={syncing}>
            {syncing ? 'Syncing…' : 'Sync from Stripe'}
          </Button>
        </div>
        <table className="w-full min-w-[720px] text-sm">
          <thead>
            <tr className="border-b text-left">
              <th className="py-2 pr-4">Slug / Stripe price</th>
              <th className="py-2 pr-4">Title</th>
              <th className="py-2 pr-4">Cost</th>
              <th className="py-2 pr-4">On sale</th>
              <th className="py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {(data?.adminRedemptionRewards ?? []).map((reward) => (
              <RewardRow key={reward.id} reward={reward} onSaved={() => refetch()} />
            ))}
          </tbody>
        </table>
      </section>

      <section className="overflow-x-auto">
        <Label className="mb-2 block text-base">Recent redemptions</Label>
        <table className="w-full min-w-[860px] text-sm">
          <thead>
            <tr className="border-b text-left">
              <th className="py-2 pr-4">#</th>
              <th className="py-2 pr-4">Member</th>
              <th className="py-2 pr-4">Reward</th>
              <th className="py-2 pr-4">Points</th>
              <th className="py-2 pr-4">Status</th>
              <th className="py-2">Take it back</th>
            </tr>
          </thead>
          <tbody>
            {(data?.adminRedemptions ?? []).map((redemption) => (
              <RedemptionRow key={redemption.id} redemption={redemption} onChanged={() => refetch()} />
            ))}
          </tbody>
        </table>
        {(data?.adminRedemptions ?? []).length === 0 && (
          <p className="text-muted-foreground py-6 text-sm">Nobody has redeemed anything yet.</p>
        )}
      </section>
    </AdminLayout>
  );
}
